import React, { createContext, useContext, ReactNode } from 'react';
import { useToast, createToast } from '../hooks/useToast';
import { ToastProps } from '../components/ui/Toast';

interface ToastContextType {
  toasts: ToastProps[];
  addToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
  // Convenience methods
  showSuccess: (title: string, message?: string, duration?: number) => void;
  showError: (title: string, message?: string, duration?: number) => void;
  showWarning: (title: string, message?: string, duration?: number) => void;
  showInfo: (title: string, message?: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { toasts, addToast, removeToast, clearAllToasts } = useToast();

  const showSuccess = (title: string, message?: string, duration?: number) => {
    addToast(createToast.success(title, message, duration));
  };

  const showError = (title: string, message?: string, duration?: number) => {
    addToast(createToast.error(title, message, duration));
  };

  const showWarning = (title: string, message?: string, duration?: number) => {
    addToast(createToast.warning(title, message, duration));
  };

  const showInfo = (title: string, message?: string, duration?: number) => {
    addToast(createToast.info(title, message, duration));
  };

  const value: ToastContextType = {
    toasts,
    addToast,
    removeToast,
    clearAllToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToastContext = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};